import type { AxiosError } from "axios";
import Axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import styled from "styled-components/macro";
import useSWR from "swr";
import type { BlockLeadFormData } from "../../../components/Buttons/Buttons";
import {
  CancelButton,
  LinkButtonLargeV2,
  PrimaryButtonMedium,
} from "../../../components/Buttons/Buttons";
import { CreateNewTenantFormContainer } from "../../../components/CreateNewTenantForm/CreateNewTenantFormContainer";
import { CreateNewUser } from "../../../components/CreateNewUser/CreateNewUser";
import { LeadAssignmentInfo } from "../../../components/LeadAssignmentInfo";
import { DelayedSpinner } from "../../../components/DelayedSpinner/DelayedSpinner";
import { DropDownButton } from "../../../components/DropDownButton/DropDownButton";
import { ErrorPlaceholder } from "../../../components/Error";
import { WarningMessageBox } from "../../../components/Form/Form";
import { CaretLeft, WarningIcon } from "../../../components/Icons/Icons";
import { IDDesktop } from "../../../components/IDs/IDs";
import { InfoBlockExtraSmall } from "../../../components/InfoBlocks/InfoBlocks";
import { HeaderLeft, HeaderRight } from "../../../components/Layout/Layout";
import {
  AddressBlock,
  ContactBlockForLead,
} from "../../../components/LocationsList/LocationsList";
import { Modal } from "../../../components/Modal/Modal";
import { Notifications } from "../../../components/Notifications/NotificationsContext";
import { SlideOut } from "../../../components/SlideOut/SlideOut";
import { StatusRight } from "../../../components/Status/Status";
import { H3 } from "../../../components/Typography/Typography";
import { endpoints } from "../../../endpoints";
import {
  Card,
  ContentWrapper,
  FullWidthHorizontalSeparator,
  PageHeader,
  PageTitle,
} from "../../../layout/portalPageLayout";
import { PageWrapper } from "../../../layout/publicPageLayout";
import { WideDetails } from "../../../layout/shared/DetailPageLayout/DetailPageLayout";
import type {
  BlockLeadData,
  Lead,
  LeadModalType,
  LeadRejectionArgsSchema,
  LeadTenantType,
  LocationState,
  Tenant,
  UserTypeChip,
  UUID,
} from "../../../types/types";
import { useRoutePath } from "../../../util/Routing";
import { useAuthenticatedUser } from "../../../hooks/useAuthenticatedUser";
import {
  formatDateTime,
  makeUrlWithParams,
  useInAppNotifications,
  useStoreState,
} from "../../../util/util";
import type { blockLeadLabels } from "../../admin/SellerAdmin/SellerAdminSampleRequestLeadDetail/SellerAdminSampleRequestLeadDetail";
import {
  ActiveWarningMessage,
  NotificationContainer,
} from "../SellerQuoteDetailPage/SellerQuoteDetailPageContent";
import { AssignLeadToUser } from "./AssignLeadToUser";
import { AssignLeadSubscribers } from "./AssignLeadSubscribers";
import { BlockLeadToUserModal } from "./BlockLeadToUserModal";
import type { HandleLeadNoteSubmitProps } from "./LeadNotes";
import { LeadNotes } from "./LeadNotes";
import { LeadRejectionModal } from "./LeadRejectionModal";
import { getLeadStatusDetails } from "./leadsUtilities";
import { LeadToBlockModal } from "./LeadToBlockModal";
import { RightButtonContainer } from "./QuoteRequestLeadDetailPage/QuoteRequestLeadDetailPage";
import type { IForwardLead } from "./ForwardToDistributor";
import { ForwardToDistributor } from "./ForwardToDistributor";
import { useAuthContext } from "../../../components/Auth";
import type { FollowedExternallyFormValues } from "./FollowedExternallyModal";
import { FollowedExternallyModal } from "./FollowedExternallyModal";
import { LeadIntelDisplay } from "./LeadIntelDisplay";
import { AlchemistInsightsBanner } from "../../../components/AlchemistInsightsBanner/AlchemistInsightsBanner";
import { spacingTokens } from "../../../designTokens";

const ButtonSection = styled.div`
  display: flex;
  justify-content: space-between;
`;

function displayCustomChoices(custom_choices: Record<string, string | null>) {
  return Object.entries(custom_choices).reduce<JSX.Element[]>(
    (acc, [key, value]) =>
      value
        ? [
            ...acc,
            <InfoBlockExtraSmall
              header={key}
              key={key}
              content={value ?? "--"}
            />,
          ]
        : acc,
    []
  );
}

/**
 * A shared page for viewing contact us/registration leads as a seller.
 *
 * /store/{slug}/admin/leads/contact-requests/{id}
 * /store/{slug}/admin/leads/registration/{id}
 *
 * This page loads a lead id from the URL to fetch a lead.
 */
export const LeadDetailPage = () => {
  const user = useAuthenticatedUser();
  const { roleIsDistributorAdmin } = useAuthContext();
  const { adminPath } = useRoutePath();
  const { t } = useTranslation();
  const { storefront_id } = useStoreState();
  const location = useLocation();
  const { lead_id } = useParams<{ lead_id: UUID }>();
  const leadType = location.pathname.includes("registration")
    ? "registration"
    : "contact_form";
  const history = useHistory();
  const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false);
  const [showAddToCustomerModal, setShowAddToCustomerModal] = useState(false);
  const [showLeadRejectionModal, setShowLeadRejectionModal] = useState(false);
  const [showLeadAssignmentModal, setShowLeadAssignmentModal] = useState(false);
  const [showConvertLeadModal, setShowConvertLeadModal] = useState(false);
  const [dropDownName, setDropDownName] = useState("");
  const { notifyError, notifySuccess } = useContext(Notifications);
  const { mutateNotifications } = useInAppNotifications(storefront_id, user);

  const [showSubscribersModal, setShowSubscribersModal] = useState(false);
  const closeSubscribersModal = () => setShowSubscribersModal(false);
  const openSubscribersModal = () => setShowSubscribersModal(true);

  const [tenantTypeToBeCreated, setTenantTypeToBeCreated] =
    useState<"Buyer" | "Distributor">("Buyer");

  const closeLeadRejectionModal = () => setShowLeadRejectionModal(false);
  const openLeadRejectionModal = () => setShowLeadRejectionModal(true);
  const closeCreateCustomerModal = () => setShowCreateCustomerModal(false);
  const openCreateCustomerModal = () => setShowCreateCustomerModal(true);
  const openAddUserToCustomerModal = () => setShowAddToCustomerModal(true);
  const closeAddUserToCustomerModal = () => setShowAddToCustomerModal(false);
  const closeLeadAssignmentModal = () => setShowLeadAssignmentModal(false);
  const openLeadAssignmentModal = () => setShowLeadAssignmentModal(true);
  const openConvertToLeadModal = () => setShowConvertLeadModal(true);
  const closeConvertToLeadModal = () => setShowConvertLeadModal(false);

  const [isForwardToDistributorOpen, setIsForwardToDistributorOpen] =
    useState(false);
  const openForwardToDistributor = () => setIsForwardToDistributorOpen(true);
  const closeForwardToDistributor = () => setIsForwardToDistributorOpen(false);

  const onSubscribersSuccess = () => {
    notifySuccess(t("Lead subscribers updated successfully"));
    closeSubscribersModal();
    mutateLead();
  };

  const onSubscribersError = (error: unknown) => {
    const errorMessage = (error as AxiosError)?.response?.data?.message;
    notifyError(
      errorMessage
        ? errorMessage
        : t("There was an error updating subscribers"),
      {
        error,
      }
    );
  };
  const [showFollowedExternallyDialog, setShowFollowedExternallyDialog] =
    useState(false);
  const openFollowedExternallyModal = () =>
    setShowFollowedExternallyDialog(true);
  const closeFollowedExternallyModal = () =>
    setShowFollowedExternallyDialog(false);

  const [showLeadIntelSlideOut, setShowLeadIntelSlideOut] = useState(false);
  const openLeadIntelSlideOut = () => setShowLeadIntelSlideOut(true);
  const closeLeadIntelSlideOut = () => setShowLeadIntelSlideOut(false);

  const onConvertToCustomer = (tenantId: string) => {
    mutateLead();
    closeCreateCustomerModal();
    history.push(
      `${adminPath}/${
        tenantTypeToBeCreated === "Buyer" ? "tenants" : "distributors"
      }/${tenantId}`
    );
  };

  const { data: blockLeadData, mutate: mutateBlockLeadData } = useSWR<
    BlockLeadData,
    AxiosError
  >(`/v1/storefronts/${storefront_id}/leads/${lead_id}/blocks`);

  const { data: spamStatusData } = useSWR(
    `/v1/storefronts/${storefront_id}/leads/${lead_id}/spam-status`
  );

  const isGlobalLeadSpam = spamStatusData?.is_spam;
  const isCurrentLeadSpam = blockLeadData?.items?.some(
    (block) => block.is_spam
  );
  const isActionDisable = !!blockLeadData?.items?.length;

  const onLeadNoteSubmit = async ({
    note,
    onSuccess,
  }: HandleLeadNoteSubmitProps) => {
    try {
      await Axios.post(
        `/v1/storefronts/${storefront_id}/leads/${lead_id}/notes`,
        {
          type: leadType,
          message: note,
        }
      );
      onSuccess();
      mutateLead();
      notifySuccess(t("note added successfully"));
    } catch (error) {
      notifyError(t("There was an error adding the note"));
    }
  };

  const {
    data: lead,
    error: leadError,
    mutate: mutateLead,
  } = useSWR<Lead, AxiosError>(
    `/v1/storefronts/${storefront_id}/leads/${lead_id}`
  );

  const { data: convertedToUser } = useSWR<Tenant>(
    lead?.status === "Added as User"
      ? endpoints.v1_storefronts_id_tenants_id(
          storefront_id,
          lead.parent_customer_id
        )
      : null
  );

  useEffect(() => {
    if (lead) {
      mutateNotifications();
    }
  }, [lead, mutateNotifications]);

  const isLoadingLead = !lead && !leadError;

  const isLeadRejected = lead?.status === "Rejected";

  const handleLeadRejection = async (messages: LeadRejectionArgsSchema) => {
    const action = isLeadRejected ? "restore" : "reject";
    const successMessage = isLeadRejected
      ? t("Lead restore successfully")
      : t("Lead rejected successfully");
    const errorMessage = isLeadRejected
      ? t("There was an error restoring lead")
      : t("There was an error rejecting lead");

    try {
      await Axios.post(
        `/v1/tenants/${user.tenant_id}/leads/${lead_id}/${action}`,
        messages
      );
      notifySuccess(successMessage);
      mutateLead();
      closeLeadRejectionModal();
    } catch (error) {
      notifyError(errorMessage, { error });
    }
  };

  const handleBlockLead = async (
    data: BlockLeadFormData,
    setShow: (show: boolean) => void
  ) => {
    const blockLeadLabels: blockLeadLabels = {
      block_domain: "domain",
      block_email: "email",
      block_phone_number: "phone",
    };

    const store: string[] = Object.entries(data)
      .filter(([key, value]) => {
        const excludedKeys = ["report_spam", "reasonForBlockLead"];
        return !excludedKeys.includes(key) && value;
      })
      .map(([key]) => blockLeadLabels[key as keyof blockLeadLabels]);

    try {
      const response = await Axios.post(
        `/v1/storefronts/${storefront_id}/leads/${lead_id}/blocks`,
        {
          block_types: store,
          is_spam: data?.report_spam,
          reason: data?.reasonForBlockLead,
        }
      );
      if (response?.status === 201) {
        notifySuccess(t("block lead added successfully"));
        mutateBlockLeadData();
        mutateLead();
        setShow(false);
      }
    } catch (error) {
      notifyError(t("There was an error adding the block lead"));
    }
  };

  const modalHandlers: Record<LeadModalType, () => void> = {
    convertToNew: openCreateCustomerModal,
    addToExisting: openAddUserToCustomerModal,
    forwardToDistributor: openForwardToDistributor,
    followedExternally: openFollowedExternallyModal,
  };

  const handleModalOpening = (type: LeadModalType) => {
    if (isGlobalLeadSpam) {
      openConvertToLeadModal();
      return;
    }

    const handler = modalHandlers[type];
    if (handler) handler();
  };

  const modalConfigMap: Record<
    string,
    {
      tenantType?: LeadTenantType;
      dropDownName: LeadModalType;
    }
  > = {
    [t("Followed Externally")]: {
      dropDownName: "followedExternally",
    },
    [t("Forward to Distributor")]: {
      dropDownName: "forwardToDistributor",
    },
    [t("Convert to customer")]: {
      tenantType: "Buyer",
      dropDownName: "convertToNew",
    },
    [t("Convert to distributor")]: {
      tenantType: "Distributor",
      dropDownName: "convertToNew",
    },
    [t("Add to existing customer")]: {
      tenantType: "Buyer",
      dropDownName: "addToExisting",
    },
    [t("Add to existing distributor")]: {
      tenantType: "Distributor",
      dropDownName: "addToExisting",
    },
  };

  const handleDropdownClick = (item: string) => {
    const config = modalConfigMap[item];
    if (!config) return;

    const { tenantType, dropDownName } = config;

    if (tenantType) {
      setTenantTypeToBeCreated(tenantType);
    }

    setDropDownName(dropDownName);
    handleModalOpening(dropDownName);
  };

  const handleConvertToLead = () => {
    setShowConvertLeadModal(false);
    if (dropDownName) {
      modalHandlers[dropDownName as LeadModalType]?.();
    }
  };

  const handleForwardToDistributor = async (
    data: IForwardLead
  ): Promise<void> => {
    try {
      const response = await Axios.post(
        endpoints.v2_storefronts_storefront_id_leads_lead_id_forward(
          storefront_id,
          lead_id
        ),
        {
          distributor_id: data.distributor.value,
          message: data.reasonForForwardLead,
          type: leadType,
        }
      );

      if (response?.status === 201) {
        notifySuccess(t("Forwarded successfully"));
        mutateLead();
      }
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    } finally {
      closeForwardToDistributor();
    }
  };

  const handleSkipAdding = async () => {
    try {
      const response = await Axios.patch(
        endpoints.v1_storefronts_storefront_id_leads_lead_id_accept(
          storefront_id,
          lead_id
        )
      );

      if (response?.status === 201) {
        notifySuccess(t("Lead request accepted successfully"));
        mutateLead();
      }
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    } finally {
      closeAddUserToCustomerModal();
    }
  };

  const handleAcceptLead = () => {
    handleDropdownClick("Add to existing distributor");
  };

  const handleFollowedExternally = async (
    data: FollowedExternallyFormValues
  ) => {
    const channels = ["email", "phone", "fax", "text"];
    const selectedChannelsString = channels
      .filter((key) => data[key] === true)
      .toString();

    try {
      const response = await Axios.post(
        endpoints.v2_storefronts_storefront_id_leads_lead_id_followed_externally(
          storefront_id,
          lead_id
        ),
        {
          followed_externally_via: selectedChannelsString,
          notes: data.reasonForFollowedExternally,
        }
      );
      if (response?.status === 201) {
        notifySuccess(t("Lead marked as followed up externally"));
        mutateLead();
        closeFollowedExternallyModal();
      }
    } catch (error) {
      notifyError(t((error as AxiosError)?.response?.data?.message));
    }
  };

  if (isLoadingLead) {
    return <DelayedSpinner />;
  }

  if (leadError) {
    return (
      <ErrorPlaceholder message={t("There was an error loading the lead")} />
    );
  }

  if (lead) {
    const contactRequestOrRegistration =
      lead.source === "registration" ? t("Registration") : t("Contact Us");

    const registrationTitle = isLeadRejected
      ? t("Are you sure you want to restore this Registration Request?")
      : t("Are you sure you want to reject this Registration Request?");

    const contactTitle = isLeadRejected
      ? t("Are you sure you want to restore this Contact Request?")
      : t("Are you sure you want to reject this Contact Request?");

    const titleString =
      lead.source === "registration" ? registrationTitle : contactTitle;

    const isModificationDisabled =
      isActionDisable ||
      lead.status === "Sent to Distributor" ||
      lead.status === "Rejected by Distributor";

    const messageTitleString =
      lead.source === "registration"
        ? t("What brings you to register today?")
        : t("How can we help you?");

    const shouldShowAssigneeSection =
      lead.status !== "Sent to Distributor" &&
      lead.status !== "Rejected by Distributor" &&
      lead.status !== "Accepted by Distributor";

    const address = "address" in lead &&
      lead.address !== null && {
        city: lead?.address?.city,
        state: lead?.address?.state,
        state_display_name: lead?.address?.state_display_name,
        country: lead?.address?.country,
        address1: lead.address?.address1,
        address2: lead.address?.address2,
        postal_code: lead.address?.postal_code,
        email_address: lead.buyer_email,
        first_name: lead.buyer_first_name,
        last_name: lead.buyer_last_name,
        phone_number: lead.buyer_phone_number,
      };

    const buyerInfo = {
      company_name: lead.buyer_company_name,
      email_address: lead.buyer_email,
      first_name: lead.buyer_first_name,
      last_name: lead.buyer_last_name,
      phone_number: lead.buyer_phone_number,
    };

    const onLeadAssignmentSuccess = (state: "unassigned" | "assigned") => {
      state === "assigned"
        ? notifySuccess(t("Lead assigned successfully"))
        : notifySuccess(t("Lead unassigned successfully"));

      closeLeadAssignmentModal();
      mutateLead();
    };

    const onLeadAssignmentFailure = (error: unknown) => {
      notifyError(t("There was an error assigning the lead"), { error });
    };

    const leadAssignmentUrl = `/v2/storefronts/${storefront_id}/leads/${lead_id}/assign`;
    const leadSubscribersUrl = endpoints.v2_storefronts_id_leads_id_subscribers(
      storefront_id,
      lead_id
    );

    const getBackLinkState = () => {
      const listState = (location.state as LocationState)?.listState;
      return listState ? { listState } : undefined;
    };

    const getBackLinkParams = () => {
      const { tabIndex, offset, perPage } =
        (location.state as LocationState)?.listState || {};
      return {
        tab: tabIndex !== undefined ? tabIndex : undefined,
        offset: offset !== undefined ? offset : undefined,
        perPage: perPage !== undefined ? perPage : undefined,
      };
    };

    const handleBackClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      const state = getBackLinkState();
      const params = getBackLinkParams();
      const search = makeUrlWithParams("", params);
      history.push({
        pathname: `${adminPath}/leads`,
        search,
        state,
      });
    };
    const userTypeChips: UserTypeChip[] =
      tenantTypeToBeCreated === "Buyer"
        ? [{ name: t("Admin"), id: "Buyer Admin" }]
        : [{ name: t("Distributor"), id: "Distributor Admin" }];

    const dropdownItems = [
      t("Convert to customer"),
      t("Convert to distributor"),
      t("Add to existing customer"),
      t("Add to existing distributor"),
      t("Forward to Distributor"),
      t("Followed Externally"),
    ];

    return (
      <PageWrapper style={{ maxWidth: "932px" }}>
        <Link to={`${adminPath}/leads`} onClick={handleBackClick}>
          <LinkButtonLargeV2>
            <CaretLeft fill="currentColor" />
            {t("Leads")}
          </LinkButtonLargeV2>
        </Link>
        <SlideOut
          title={
            roleIsDistributorAdmin ? t("Assign") : t("Assign Lead To User")
          }
          show={showLeadAssignmentModal}
          closeFlyout={closeLeadAssignmentModal}
        >
          <AssignLeadToUser
            url={leadAssignmentUrl}
            onSuccess={onLeadAssignmentSuccess}
            onError={onLeadAssignmentFailure}
            prefilledUser={lead?.assignee}
            distributorId={user.tenant_id}
          />
        </SlideOut>
        <SlideOut
          show={showSubscribersModal}
          closeFlyout={closeSubscribersModal}
          title={t("Assign Lead Subscriber(s)")}
        >
          <AssignLeadSubscribers
            url={leadSubscribersUrl}
            initialSubscribers={lead.subscribers || []}
            onSuccess={onSubscribersSuccess}
            onError={onSubscribersError}
          />
        </SlideOut>
        <PageHeader>
          <HeaderLeft>
            <PageTitle>{contactRequestOrRegistration}</PageTitle>
            {lead.number && <IDDesktop>{lead.number}</IDDesktop>}
          </HeaderLeft>
          <HeaderRight>
            <StatusRight {...getLeadStatusDetails(lead.status, t)} />
          </HeaderRight>
        </PageHeader>
        <LeadAssignmentInfo
          assignee={lead.assignee}
          assignmentDate={lead.assignment_date}
          subscribers={lead.subscribers}
          subscribersUpdatedOn={lead.subscribers_updated_on}
          subscriber_assignment_type={lead.subscriber_assignment_type}
          shouldShowAssigneeSection={shouldShowAssigneeSection}
          onOpenLeadAssignmentModal={openLeadAssignmentModal}
          onOpenSubscribersModal={openSubscribersModal}
          isActionDisabled={isModificationDisabled}
          isModificationDisabled={isModificationDisabled}
        />
        <FullWidthHorizontalSeparator />

        <ContentWrapper
          style={{ margin: `45px 0 ${spacingTokens.spacing_2xl} 0` }}
        >
          {lead.lead_intel && (
            <AlchemistInsightsBanner onViewDetails={openLeadIntelSlideOut} />
          )}
          <Card>
            <H3>{t("Customer Details")}</H3>
            <WideDetails>
              <InfoBlockExtraSmall
                header={t("Requested By")}
                content={<ContactBlockForLead address={buyerInfo} />}
              />
              {"address" in lead && address && (
                <InfoBlockExtraSmall
                  header={t("Address")}
                  content={<AddressBlock address={address} />}
                />
              )}
              <div>
                <InfoBlockExtraSmall
                  header={t("Date Received")}
                  content={formatDateTime(lead.created_at)}
                />
                {lead.status === "Added as User" && convertedToUser && (
                  <div style={{ marginTop: "5px" }}>
                    <InfoBlockExtraSmall
                      header={t("User Added to")}
                      content={convertedToUser.name}
                    />
                  </div>
                )}
              </div>
            </WideDetails>

            {lead?.extra?.message && (
              <div style={{ marginBottom: spacingTokens.spacing_2xl }}>
                <InfoBlockExtraSmall
                  header={messageTitleString}
                  content={lead.extra.message}
                />
              </div>
            )}
            {lead.extra?.custom_choices && (
              <div>{displayCustomChoices(lead.extra.custom_choices)}</div>
            )}

            {lead.status === "New" && lead.is_existing_customer && (
              <WarningMessageBox>
                <WarningIcon width={18} />
                {t(
                  `This lead may be a user of an existing customer, {{companyName}}. Click Add to Existing Customer to add this lead as a user to {{companyName}}.`,
                  {
                    companyName: lead.company_name,
                  }
                )}
              </WarningMessageBox>
            )}

            {(isCurrentLeadSpam || isGlobalLeadSpam) && (
              <NotificationContainer>
                <WarningIcon width={18} height={18} />
                <ActiveWarningMessage>
                  {t(
                    `This lead may be spam. Please review the details carefully before accepting this lead.`
                  )}
                </ActiveWarningMessage>
              </NotificationContainer>
            )}

            <ButtonSection>
              {(lead.status === "New" || lead.status === "blocked") && (
                <div>
                  <CancelButton
                    style={{ paddingLeft: spacingTokens.spacing_none }}
                    data-testid="reject-lead-button"
                    onClick={openLeadRejectionModal}
                    disabled={isActionDisable}
                  >
                    {t("Reject this Lead")}
                  </CancelButton>
                  {!roleIsDistributorAdmin && (
                    <LeadToBlockModal
                      handleBlockLead={handleBlockLead}
                      blockLeadData={blockLeadData}
                    />
                  )}
                </div>
              )}
              {(lead.status === "New" || lead.status === "blocked") && (
                <div>
                  <RightButtonContainer>
                    {roleIsDistributorAdmin ? (
                      <PrimaryButtonMedium
                        style={{ height: "45px" }}
                        onClick={handleAcceptLead}
                      >
                        {t("Accept")}
                      </PrimaryButtonMedium>
                    ) : (
                      <DropDownButton
                        items={dropdownItems}
                        textLeft={t("Accept")}
                        clickHandler={(item: string) => {
                          handleDropdownClick(item);
                        }}
                        isDisabled={isActionDisable}
                        style={{ padding: "11px 50px" }}
                      />
                    )}
                  </RightButtonContainer>

                  {showConvertLeadModal && (
                    <BlockLeadToUserModal
                      showConvertLeadModal={showConvertLeadModal}
                      handleCancel={closeConvertToLeadModal}
                      handleConvertToLead={handleConvertToLead}
                      setShowConvertLeadModal={setShowConvertLeadModal}
                    />
                  )}
                </div>
              )}
            </ButtonSection>

            <SlideOut
              show={isForwardToDistributorOpen}
              closeFlyout={closeForwardToDistributor}
              title={t("Forward to Distributor")}
            >
              <ForwardToDistributor
                handleForwardToDistributor={handleForwardToDistributor}
              />
            </SlideOut>

            {isLeadRejected && (
              <PrimaryButtonMedium
                style={{
                  marginTop: "30px",
                }}
                onClick={openLeadRejectionModal}
              >
                {t("Restore to New")}
              </PrimaryButtonMedium>
            )}
          </Card>
          <LeadRejectionModal
            show={showLeadRejectionModal}
            closeModal={closeLeadRejectionModal}
            onFormSubmit={handleLeadRejection}
            titleString={titleString}
            isLeadRejected={isLeadRejected}
          />
          <FollowedExternallyModal
            showModal={showFollowedExternallyDialog}
            closeModal={closeFollowedExternallyModal}
            handleFollowedExternally={handleFollowedExternally}
          />
          <LeadNotes
            internal_notes={lead.internal_notes}
            handleSubmit={onLeadNoteSubmit}
          />
        </ContentWrapper>
        <SlideOut
          title={t("Lead Intelligence")}
          show={showLeadIntelSlideOut}
          closeFlyout={closeLeadIntelSlideOut}
        >
          {lead.lead_intel && (
            <LeadIntelDisplay leadIntel={lead.lead_intel} lead={lead} />
          )}
        </SlideOut>
        <Modal
          closeModal={closeAddUserToCustomerModal}
          show={showAddToCustomerModal}
        >
          <CreateNewUser
            tenantId={user.tenant_id}
            userTypeChips={userTypeChips}
            defaultUserType={userTypeChips[0].id}
            leadToUserData={{
              firstname: lead.buyer_first_name,
              lastname: lead.buyer_last_name,
              email_address: lead.buyer_email,
              phone_number: lead.buyer_phone_number,
              lead_id: lead.id,
              existing_customer: {
                company_name: lead.company_name,
                customer_id: lead.customer_id,
              },
              address: lead.address,
            }}
            tenantTypeToFetch={tenantTypeToBeCreated}
            userFormType="add_to_Existing_customer"
            onSuccess={() => {
              mutateLead();
              closeAddUserToCustomerModal();
            }}
            showShippingAddress={true}
            isLeadForwarded={roleIsDistributorAdmin}
            handleSkipAdding={handleSkipAdding}
            onCancel={closeAddUserToCustomerModal}
          />
        </Modal>
        <Modal
          closeModal={closeCreateCustomerModal}
          show={showCreateCustomerModal}
        >
          <CreateNewTenantFormContainer
            onSuccess={onConvertToCustomer}
            lead={lead}
            sellerTenantId={user.tenant_id}
            type={tenantTypeToBeCreated}
            onCancel={closeCreateCustomerModal}
          />
        </Modal>
      </PageWrapper>
    );
  } else return null;
};
